import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  User, Mail, Phone, Calendar, Package, Heart, Gift, 
  Edit2, Check, X, ChevronRight, Clock, Coffee, Loader2,
  ShoppingBag, Star, Cake, LogOut, BellRing, Bell, Crown, Wifi, WifiOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { useFavorites } from '@/context/FavoritesContext';
import { usePushNotifications } from '@/context/PushNotificationContext';
import { useOrderStatus } from '@/context/OrderStatusContext';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, updateUser } = useAuth();
  const { favorites } = useFavorites();
  const { isSupported: pushSupported, isSubscribed, subscribe, unsubscribe, permission } = usePushNotifications();
  const { isConnected: wsConnected, connectToUserOrders, lastUpdate, orderStatuses } = useOrderStatus();
  const [activeTab, setActiveTab] = useState('overview');
  const [orders, setOrders] = useState([]);
  const [loyaltyStatus, setLoyaltyStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    birthday: ''
  });

  // Connect to WebSocket for real-time order updates
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      connectToUserOrders(user.id);
    }
  }, [isAuthenticated, user?.id, connectToUserOrders]);

  // Update orders when receiving WebSocket updates
  useEffect(() => {
    if (lastUpdate && lastUpdate.order) {
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === lastUpdate.order_id 
            ? { ...order, status: lastUpdate.status, updated_at: lastUpdate.order.updated_at }
            : order
        )
      );
    }
  }, [lastUpdate]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, user?.id]);

  const fetchUserData = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      // Fetch orders and loyalty status in parallel
      const [ordersRes, loyaltyRes] = await Promise.allSettled([
        api.get(`/api/orders/user/${user.id}`),
        api.get(`/api/loyalty/${user.id}`)
      ]);

      if (ordersRes.status === 'fulfilled') {
        setOrders(ordersRes.value.data);
      }

      if (loyaltyRes.status === 'fulfilled') {
        setLoyaltyStatus(loyaltyRes.value.data);
      }
    } catch (err) {
      console.error('Failed to fetch user data:', err);
    }
    setLoading(false);
  };

  const handleEditProfile = () => {
    setEditForm({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      birthday: user?.birthday || ''
    });
    setEditing(true);
  };

  const handleSaveProfile = async () => {
    // Don't send birthday if user already has one (prevent modification)
    const updateData = {
      name: editForm.name,
      email: editForm.email,
      phone: editForm.phone
    };
    
    // Only include birthday if user doesn't have one yet
    if (!user?.birthday && editForm.birthday) {
      updateData.birthday = editForm.birthday;
    }
    
    try {
      await api.put(`/api/users/${user.id}`, updateData);
      updateUser(updateData);
      toast.success('Profile updated!');
      setEditing(false);
    } catch (err) {
      toast.error('Failed to update profile');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      confirmed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      preparing: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      ready: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      completed: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
    };
    return colors[status] || colors.pending;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return null;
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'orders', label: 'Order History', icon: Package },
    { id: 'favorites', label: 'Favorites', icon: Heart },
    { id: 'rewards', label: 'Rewards', icon: Gift }
  ];

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="profile-page">
      {/* Profile Header */}
      <section className="py-8 bg-gradient-to-b from-primary/10 to-transparent">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center">
                <User className="w-10 h-10 text-primary" />
              </div>
              <div>
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="font-heading text-3xl md:text-4xl text-foreground"
                  data-testid="profile-name"
                >
                  {user?.name}
                </motion.h1>
                <p className="text-muted-foreground" data-testid="profile-email">{user?.email}</p>
                {loyaltyStatus?.vip_status?.is_vip ? (
                  <span className="inline-flex items-center gap-1 mt-2 px-4 py-1.5 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-full text-sm font-bold shadow-lg" data-testid="vip-badge">
                    <Crown className="w-4 h-4" />
                    VIP Member - 10% Off For Life
                  </span>
                ) : (
                  loyaltyStatus?.vip_status && (
                    <div className="mt-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>${loyaltyStatus.vip_status.amount_to_vip.toFixed(2)} FJD to VIP status</span>
                      </div>
                      <div className="w-48 h-2 bg-muted rounded-full mt-1 overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full transition-all duration-500"
                          style={{ width: `${loyaltyStatus.vip_status.progress_percentage}%` }}
                        />
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={handleLogout}
              className="rounded-full"
              data-testid="profile-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b border-border sticky top-20 bg-background/95 backdrop-blur-lg z-40">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`profile-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'border-primary text-primary' 
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-16">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground mt-4">Loading your profile...</p>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-card p-4 rounded-2xl border border-border text-center">
                      <Package className="w-6 h-6 mx-auto mb-2 text-primary" />
                      <p className="text-2xl font-bold" data-testid="stat-orders">{orders.length}</p>
                      <p className="text-sm text-muted-foreground">Orders</p>
                    </div>
                    <div className="bg-card p-4 rounded-2xl border border-border text-center">
                      <Heart className="w-6 h-6 mx-auto mb-2 text-red-500" />
                      <p className="text-2xl font-bold" data-testid="stat-favorites">{favorites?.length || 0}</p>
                      <p className="text-sm text-muted-foreground">Favorites</p>
                    </div>
                    <div className="bg-card p-4 rounded-2xl border border-border text-center">
                      <Coffee className="w-6 h-6 mx-auto mb-2 text-amber-600" />
                      <p className="text-2xl font-bold" data-testid="stat-coffees">{loyaltyStatus?.coffee_count || 0}</p>
                      <p className="text-sm text-muted-foreground">Coffees</p>
                    </div>
                    <div className="bg-card p-4 rounded-2xl border border-border text-center">
                      <Gift className="w-6 h-6 mx-auto mb-2 text-green-500" />
                      <p className="text-2xl font-bold" data-testid="stat-free-coffees">{loyaltyStatus?.free_coffees_available || 0}</p>
                      <p className="text-sm text-muted-foreground">Free Coffees</p>
                    </div>
                  </div>

                  {/* Profile Info */}
                  <div className="bg-card p-6 rounded-2xl border border-border">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-medium">Profile Information</h2>
                      {!editing && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={handleEditProfile}
                          data-testid="edit-profile-btn"
                        >
                          <Edit2 className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                      )}
                    </div>

                    {editing ? (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Name</Label>
                          <Input
                            value={editForm.name}
                            onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                            data-testid="edit-name-input"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={editForm.email || user?.email || ''}
                            onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="email@example.com"
                            data-testid="edit-email-input"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Phone</Label>
                          <Input
                            value={editForm.phone}
                            onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                            placeholder="+679 123 4567"
                            data-testid="edit-phone-input"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Birthday</Label>
                          <Input
                            type="date"
                            value={editForm.birthday}
                            disabled={!!user?.birthday}
                            className={user?.birthday ? 'bg-muted cursor-not-allowed' : ''}
                            onChange={(e) => setEditForm(prev => ({ ...prev, birthday: e.target.value }))}
                            data-testid="edit-birthday-input"
                          />
                          {user?.birthday && (
                            <p className="text-xs text-muted-foreground">Birthday cannot be changed. Contact support if needed.</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={handleSaveProfile} data-testid="save-profile-btn">
                            <Check className="w-4 h-4 mr-2" />
                            Save Changes
                          </Button>
                          <Button variant="outline" onClick={() => setEditing(false)}>
                            <X className="w-4 h-4 mr-2" />
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <Mail className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <p className="text-sm text-muted-foreground">Email</p>
                            <p className="font-medium">{user?.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Phone className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <p className="text-sm text-muted-foreground">Phone</p>
                            <p className="font-medium">{user?.phone || 'Not set'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Cake className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <p className="text-sm text-muted-foreground">Birthday</p>
                            <p className="font-medium">
                              {user?.birthday ? new Date(user.birthday).toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : 'Not set'}
                              {loyaltyStatus?.birthday_coffee_available && (
                                <span className="ml-2 text-primary text-sm">🎂 Free coffee today!</span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Notification Preferences */}
                  {pushSupported && (
                    <div className="bg-card p-6 rounded-2xl border border-border">
                      <h2 className="text-lg font-medium mb-4">Notification Settings</h2>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <BellRing className="w-5 h-5 text-violet-500" />
                            <div>
                              <p className="font-medium">Browser Push Notifications</p>
                              <p className="text-sm text-muted-foreground">Get instant alerts for order updates</p>
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              if (isSubscribed) {
                                const success = await unsubscribe();
                                if (success) toast.success('Notifications disabled');
                              } else {
                                const success = await subscribe(user?.id);
                                if (success) {
                                  toast.success('Notifications enabled!');
                                } else {
                                  toast.error('Please allow notifications in your browser settings');
                                }
                              }
                            }}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                              isSubscribed 
                                ? 'bg-primary text-white' 
                                : 'bg-muted hover:bg-muted/80'
                            }`}
                            data-testid="push-toggle-btn"
                          >
                            {isSubscribed ? 'Enabled' : 'Enable'}
                          </button>
                        </div>
                        {permission === 'denied' && (
                          <p className="text-sm text-amber-600 dark:text-amber-400">
                            Notifications are blocked. Please enable them in your browser settings.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Recent Orders Preview */}
                  {orders.length > 0 && (
                    <div className="bg-card p-6 rounded-2xl border border-border">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-medium">Recent Orders</h2>
                        <button 
                          onClick={() => setActiveTab('orders')}
                          className="text-sm text-primary hover:underline flex items-center gap-1"
                        >
                          View All <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="space-y-3">
                        {orders.slice(0, 3).map(order => (
                          <div 
                            key={order.id} 
                            className="flex items-center justify-between p-3 bg-muted/50 rounded-xl"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                <ShoppingBag className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">
                                  {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {formatDate(order.created_at)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">${order.total?.toFixed(2)}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(order.status)}`}>
                                {order.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Orders Tab */}
              {activeTab === 'orders' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-medium">Order History</h2>
                    <div className="flex items-center gap-2 text-sm">
                      {wsConnected ? (
                        <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                          <Wifi className="w-4 h-4" />
                          <span className="hidden sm:inline">Live Updates</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <WifiOff className="w-4 h-4" />
                          <span className="hidden sm:inline">Offline</span>
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {orders.length === 0 ? (
                    <div className="text-center py-16 bg-card rounded-2xl border border-border">
                      <Package className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                      <h3 className="font-heading text-xl mb-2">No orders yet</h3>
                      <p className="text-muted-foreground mb-6">Start your coffee journey with us!</p>
                      <Link to="/menu">
                        <Button className="rounded-full bg-primary hover:bg-primary/90">
                          <Coffee className="w-4 h-4 mr-2" />
                          Browse Menu
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {orders.map((order, i) => (
                        <motion.div
                          key={order.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className={`bg-card p-5 rounded-2xl border transition-all duration-300 ${
                            lastUpdate?.order_id === order.id && Date.now() - new Date(lastUpdate.timestamp).getTime() < 5000
                              ? 'border-primary ring-2 ring-primary/20'
                              : 'border-border'
                          }`}
                          data-testid={`order-${order.id}`}
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">Order</span>
                                <span className="font-mono text-sm font-medium">#{order.id.slice(0, 8)}</span>
                                {lastUpdate?.order_id === order.id && Date.now() - new Date(lastUpdate.timestamp).getTime() < 5000 && (
                                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full animate-pulse">
                                    Updated
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <Clock className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">
                                  {formatDate(order.created_at)}
                                </span>
                              </div>
                            </div>
                            <AnimatePresence mode="wait">
                              <motion.span
                                key={order.status}
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.8, opacity: 0 }}
                                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}
                              >
                                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                              </motion.span>
                            </AnimatePresence>
                          </div>

                          <div className="space-y-2 mb-4">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span>
                                  {item.quantity}x {item.name}
                                  {item.selected_size && ` (${item.selected_size})`}
                                </span>
                                <span className="text-muted-foreground">
                                  ${(item.price * item.quantity).toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>

                          <div className="flex items-center justify-between pt-4 border-t border-border">
                            <span className="font-medium">Total</span>
                            <span className="text-lg font-bold text-primary">${order.total?.toFixed(2)} FJD</span>
                          </div>

                          {order.payment_method && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              Payment: {order.payment_method === 'counter' ? 'Pay at Counter' : order.payment_method.toUpperCase()}
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Favorites Tab */}
              {activeTab === 'favorites' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-medium">Your Favorites</h2>
                    <Link to="/favorites">
                      <Button variant="outline" className="rounded-full">
                        Manage Favorites
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>

                  {!favorites || favorites.length === 0 ? (
                    <div className="text-center py-16 bg-card rounded-2xl border border-border">
                      <Heart className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                      <h3 className="font-heading text-xl mb-2">No favorites yet</h3>
                      <p className="text-muted-foreground mb-6">Save your go-to orders for quick reordering!</p>
                      <Link to="/menu">
                        <Button className="rounded-full bg-primary hover:bg-primary/90">
                          <Coffee className="w-4 h-4 mr-2" />
                          Browse Menu
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                      {favorites.slice(0, 6).map((fav, i) => (
                        <motion.div
                          key={fav.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className="bg-card p-4 rounded-2xl border border-border flex gap-4"
                        >
                          <img 
                            src={fav.item_image} 
                            alt={fav.item_name}
                            className="w-20 h-20 rounded-xl object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium truncate">{fav.nickname || fav.item_name}</h3>
                            {fav.nickname && (
                              <p className="text-sm text-muted-foreground truncate">{fav.item_name}</p>
                            )}
                            <p className="text-primary font-medium mt-1">${fav.current_price?.toFixed(2)} FJD</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Rewards Tab */}
              {activeTab === 'rewards' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <h2 className="text-xl font-medium">Your Rewards</h2>

                  {/* VIP Status Progress */}
                  <div className="bg-card p-6 rounded-2xl border border-border">
                    <div className="flex items-center gap-3 mb-4">
                      <Crown className="w-6 h-6 text-amber-500" />
                      <h3 className="font-medium">VIP Status Progress</h3>
                    </div>
                    
                    {loyaltyStatus?.vip_status?.is_vip ? (
                      <div className="bg-gradient-to-r from-amber-100 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-900/30 p-6 rounded-xl text-center">
                        <Crown className="w-12 h-12 mx-auto mb-3 text-amber-500" />
                        <p className="font-bold text-xl text-amber-700 dark:text-amber-400">You're a VIP Member!</p>
                        <p className="text-amber-600 dark:text-amber-300 mt-2">Enjoy 10% off for life on all orders - online or in the cafe!</p>
                        <p className="text-sm text-amber-500 dark:text-amber-400 mt-3">Total Spent: ${loyaltyStatus.total_spent?.toFixed(2)} FJD</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span>Progress to VIP</span>
                            <span className="font-medium">{loyaltyStatus?.total_spent?.toFixed(2) || '0.00'} / 500 FJD</span>
                          </div>
                          <div className="h-4 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full transition-all duration-500"
                              style={{ width: `${loyaltyStatus?.vip_status?.progress_percentage || 0}%` }}
                            />
                          </div>
                          <p className="text-sm text-muted-foreground mt-2">
                            <span className="font-medium text-amber-600 dark:text-amber-400">{loyaltyStatus?.vip_status?.amount_to_vip?.toFixed(2) || '500.00'} FJD</span> more to unlock VIP status and get 10% off for life!
                          </p>
                        </div>
                        
                        <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-xl">
                          <p className="text-sm text-amber-800 dark:text-amber-200">
                            <strong>VIP Benefits:</strong> Spend 500 FJD total to become a VIP member and receive 10% off on every order for life - both online and in the cafe!
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Coffee Progress */}
                  <div className="bg-card p-6 rounded-2xl border border-border">
                    <h3 className="font-medium mb-4">Coffee Rewards</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span>Coffees Purchased</span>
                        <span className="font-bold text-xl">{loyaltyStatus?.coffee_count || 0}</span>
                      </div>
                      <div className="flex gap-1">
                        {[...Array(10)].map((_, i) => (
                          <div 
                            key={i}
                            className={`flex-1 h-8 rounded-lg flex items-center justify-center ${
                              i < (loyaltyStatus?.coffee_count || 0) % 10 
                                ? 'bg-primary text-white' 
                                : 'bg-muted'
                            }`}
                          >
                            <Coffee className="w-4 h-4" />
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {10 - ((loyaltyStatus?.coffee_count || 0) % 10)} more coffees until your next free one!
                      </p>
                      {loyaltyStatus?.free_coffees_available > 0 && (
                        <div className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 p-4 rounded-xl text-center">
                          <Gift className="w-8 h-8 mx-auto mb-2" />
                          <p className="font-medium">You have {loyaltyStatus.free_coffees_available} free coffee{loyaltyStatus.free_coffees_available > 1 ? 's' : ''} available!</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Birthday Reward */}
                  <div className="bg-card p-6 rounded-2xl border border-border">
                    <h3 className="font-medium mb-4">Birthday Reward</h3>
                    {user?.birthday ? (
                      loyaltyStatus?.birthday_coffee_available ? (
                        <div className="bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-400 p-4 rounded-xl text-center">
                          <Cake className="w-8 h-8 mx-auto mb-2" />
                          <p className="font-medium">Happy Birthday! 🎂</p>
                          <p className="text-sm">Your free birthday coffee is waiting for you!</p>
                        </div>
                      ) : (
                        <p className="text-muted-foreground">
                          Your birthday: {new Date(user.birthday).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                          <br />
                          <span className="text-sm">A free coffee will be available on your special day!</span>
                        </p>
                      )
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-muted-foreground mb-4">Add your birthday to get a free coffee!</p>
                        <Button variant="outline" onClick={handleEditProfile} className="rounded-full">
                          <Edit2 className="w-4 h-4 mr-2" />
                          Add Birthday
                        </Button>
                      </div>
                    )}
                  </div>

                  <Link to="/loyalty" className="block">
                    <Button className="w-full rounded-full bg-primary hover:bg-primary/90">
                      View Full Rewards Details
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </motion.div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
